import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

type TypedConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  affectedItems: { id: string; label: string }[];
  confirmWord: string;
  confirmButtonText?: string;
  confirmButtonColor?: 'primary' | 'error';
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export default function TypedConfirmDialog({
  open,
  title,
  description,
  affectedItems,
  confirmWord,
  confirmButtonText = 'Confirm',
  confirmButtonColor = 'primary',
  loading = false,
  onConfirm,
  onCancel,
}: TypedConfirmDialogProps) {
  const [input, setInput] = useState('');
  const normalizedInput = input.trim().toLowerCase();
  const normalizedWord = confirmWord.trim().toLowerCase();
  const canConfirm = normalizedInput === normalizedWord && normalizedWord !== '';

  useEffect(() => {
    if (open) setInput('');
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{description}</DialogContentText>
        <Box component="ul" mt={1} pl={2}>
          {affectedItems.slice(0, 20).map((item) => (
            <li key={item.id}>
              <Typography variant="body2">{item.label}</Typography>
            </li>
          ))}
          {affectedItems.length > 20 && (
            <li>
              <Typography variant="body2">...and {affectedItems.length - 20} more</Typography>
            </li>
          )}
        </Box>
        <TextField
          fullWidth
          size="small"
          margin="normal"
          label={`Type '${confirmWord}' to confirm`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color={confirmButtonColor} disabled={!canConfirm || loading}>
          {loading ? <CircularProgress size={16} color="inherit" /> : confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
